/**
 * D-4 (2026-06-02) — scenario hero images.
 *
 * Maps each story paragraph to one of 6 key_locations from
 * `seed/scenarios/dr_no_adventure.json`. Paragraphs not in the map render
 * text-only (no image). Mapping is strict: only paragraphs whose own text
 * EXPLICITLY names the location (Picasso/safe → office, 8-cells →
 * cell_block, console+Middle East map → control_room_lab, sea sled/dock
 * → the_island, staircase-down/reactor switch → underground, Tobruk →
 * libya_tobruk).
 *
 * Image source provenance:
 * - libya_tobruk: live.staticflickr.com (real 1960s Tobruk harbor)
 * - the_island: jamesbond.fandom.com (Crab Key dock aerial)
 * - ben_gazi_office: 2.bp.blogspot.com (Dr. No 1962 lair interior)
 * - cell_block_area: commons.wikimedia.org (Prison_cell_block.jpg, CC-BY) —
 *     D-track polish (2026-06-02): replaced D-4 decontamination-chamber
 *     stand-in with a literal multi-cell-block image (Nikon D7000 shot of
 *     an abandoned cellblock with 5+ visible barred cells in perspective).
 *     Closer to the JSON's "eight cells holding Pamela Holck" than the
 *     decontamination chamber the 1962 film offered.
 * - control_room_lab: www.bluscreens.net (Dr. No 1962 reactor pool +
 *     DANGER LEVEL panel)
 * - underground_missile_facility: preview.redd.it (Dr. No 1962 reactor
 *     control room wide shot, RetroFuturism)
 *
 * Sourced via SearXNG `engines=bing+images` per D-3 working pattern.
 * Private-repo non-commercial fan use.
 *
 * Wire-in: hero `<img>` rendered ABOVE `.br-scenario-paragraph-header` in
 * ParagraphView (and above `<h1>` in BriefingView). Aspect-cover crop to
 * a clamp-bounded height — avoids the 1.51:1 (libya_tobruk) vs 1.78:1
 * (control_room_lab) aspect mismatch from forcing a single ratio.
 */

import { getActiveScenarioId } from '../../scenarioRegistry';
import libyaTobrukImg from '../../assets/scenes/libya_tobruk.jpg';
import theIslandImg from '../../assets/scenes/the_island.jpg';
import benGaziOfficeImg from '../../assets/scenes/ben_gazi_office.jpg';
import cellBlockAreaImg from '../../assets/scenes/cell_block_area.jpg';
import controlRoomLabImg from '../../assets/scenes/control_room_lab.jpg';
import undergroundMissileFacilityImg from '../../assets/scenes/underground_missile_facility.jpg';

export type SceneLocationId =
  | 'libya_tobruk'
  | 'the_island'
  | 'ben_gazi_office'
  | 'cell_block_area'
  | 'control_room_lab'
  | 'underground_missile_facility';

export const SCENE_IMAGE_URLS: Record<SceneLocationId, string> = {
  libya_tobruk: libyaTobrukImg,
  the_island: theIslandImg,
  ben_gazi_office: benGaziOfficeImg,
  cell_block_area: cellBlockAreaImg,
  control_room_lab: controlRoomLabImg,
  underground_missile_facility: undergroundMissileFacilityImg,
};

export const SCENE_ALT_TEXT: Record<SceneLocationId, string> = {
  libya_tobruk: 'Tobruk harbor, Libya — M.I.6 staging area',
  the_island: "The Island — Pentroc Corporation facility, Dr. No's base",
  ben_gazi_office:
    "Ahmal Ben Gazi's office — oak desk, Picasso hiding a wall safe",
  cell_block_area: 'Cell block area — eight cells holding Pamela Holck',
  control_room_lab:
    'Control room — reactor controls and Middle East missile launch panel',
  underground_missile_facility:
    'Underground missile facility — jammed ground-to-ground missiles and nuclear reactor',
};

/**
 * Briefing view scene — the M.I.6 character briefing about Tobruk + Holck.
 */
export const BRIEFING_SCENE: SceneLocationId = 'libya_tobruk';

/**
 * Per-paragraph scene anchor. Verified by reading each paragraph's verbatim
 * text in `seed/scenarios/dr_no_adventure.json:story_paragraphs[]`. Roll
 * mechanics paragraphs (¶3, ¶5, ¶6, ¶8, ¶10, …) are intentionally absent
 * — their text is dice-system instructions, not narrative scene-setting.
 */
export const PARAGRAPH_SCENE_MAP: Record<number, SceneLocationId> = {
  // ben_gazi_office — Ben Gazi appears, papers, Picasso, wall safe
  11: 'ben_gazi_office',
  17: 'ben_gazi_office',
  33: 'ben_gazi_office',
  43: 'ben_gazi_office',
  52: 'ben_gazi_office',
  62: 'ben_gazi_office',
  79: 'ben_gazi_office',
  80: 'ben_gazi_office',
  90: 'ben_gazi_office',
  93: 'ben_gazi_office',
  98: 'ben_gazi_office',
  99: 'ben_gazi_office',

  // cell_block_area — eight cells, Pamela Holck
  19: 'cell_block_area',
  22: 'cell_block_area',
  24: 'cell_block_area',
  39: 'cell_block_area',
  60: 'cell_block_area',
  87: 'cell_block_area',

  // control_room_lab — console + dials + Middle East map + Dr. No
  4: 'control_room_lab',
  20: 'control_room_lab',
  25: 'control_room_lab',
  44: 'control_room_lab',
  47: 'control_room_lab',
  53: 'control_room_lab',
  63: 'control_room_lab',
  70: 'control_room_lab',
  77: 'control_room_lab',
  85: 'control_room_lab',
  97: 'control_room_lab',

  // the_island — sea sled, dock, helicopter, shore
  1: 'the_island',
  16: 'the_island',
  41: 'the_island',
  42: 'the_island',
  65: 'the_island',
  102: 'the_island',

  // underground_missile_facility — staircase down, reactor switch
  9: 'underground_missile_facility',
  18: 'underground_missile_facility',
  36: 'underground_missile_facility',
  67: 'underground_missile_facility',
  75: 'underground_missile_facility',

  // libya_tobruk — helicopter landing at Tobruk docks
  57: 'libya_tobruk',
  59: 'libya_tobruk',
};

/**
 * Look up a paragraph's scene image. Returns null for unmapped paragraphs
 * (roll mechanics, mid-combat, transitional prose) — caller renders
 * text-only.
 *
 * C5 (2026-06-07): the per-paragraph PARAGRAPH_SCENE_MAP is keyed by
 * dr_no paragraph numbers. For other scenarios (a_view_to_a_kill /
 * diamonds_never_die / code_red) the paragraph numbers DO NOT match
 * the same scenes — returning a dr_no image for VG35014's ¶1
 * (Siberia paradrop) is worse than text-only. Gate by active scenario;
 * scene maps for the other 3 scenarios are not yet authored.
 */
export function lookupSceneImage(
  paragraphNumber: number,
): { url: string; alt: string; locationId: SceneLocationId } | null {
  if (getActiveScenarioId() !== 'dr_no_adventure') return null;
  const locationId = PARAGRAPH_SCENE_MAP[paragraphNumber];
  if (!locationId) return null;
  return {
    url: SCENE_IMAGE_URLS[locationId],
    alt: SCENE_ALT_TEXT[locationId],
    locationId,
  };
}
